import { useState, useRef, useEffect } from "react";
import Shell from "@/layout/Shell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, Bot, User, ShieldAlert } from "lucide-react";

// One turn: { role: 'user' | 'assistant', content, resourceMessage?, crisis?, safetyConcern? }
export default function Help() {
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.aiGuidance(next.map((t) => ({ role: t.role, content: t.content })));
      setTurns((cur) => [...cur, {
        role: "assistant",
        content: r?.reply || "Sorry, I didn't catch that — could you rephrase?",
        resourceMessage: r?.resource_message || null,
        crisis: !!r?.crisis,
        safetyConcern: !!r?.safety_concern,
        sources: Array.isArray(r?.sources) ? r.sources : [],
      }]);
    } catch (e) {
      toast.error(e.message || "Could not reach the assistant");
    } finally { setBusy(false); }
  };

  const sendToAdmins = async (turn) => {
    setSendingReport(true);
    try {
      // Build a short transcript so admins have context, not just the last line.
      const idx = turns.indexOf(turn);
      const recent = turns.slice(Math.max(0, idx - 4), idx + 1);
      const transcript = recent.map((t) => `${t.role === "user" ? "Them" : "Assistant"}: ${t.content}`).join("\n");
      const firstUserLine = recent.find((t) => t.role === "user")?.content || turn.content;
      await api.createTicket({
        title: (turn.crisis ? "Urgent: possible crisis — " : "Personal safety concern — ") + firstUserLine.slice(0, 80),
        description: transcript,
        priority: "urgent",
        safety_report: true,
      });
      toast.success("Sent to admins", { description: "Someone will follow up directly." });
    } catch (e) {
      toast.error(e.message || "Could not send this to admins");
    } finally { setSendingReport(false); }
  };

  return (
    <Shell role="portal" title="Ask for Help" subtitle="Anything — personal, campus, or a ticket issue. Just ask.">
      <div className="glass rounded-2xl p-0 flex flex-col h-[70vh] max-h-[720px] overflow-hidden" data-testid="help-chat">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {turns.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
              <MessageCircle className="h-8 w-8 opacity-50" />
              <p className="text-sm max-w-sm">
                Ask about anything on your mind — a class problem, a roommate issue, something about a ticket, or just advice on what to do next.
              </p>
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`flex gap-2.5 ${t.role === "user" ? "justify-end" : "justify-start"}`}>
              {t.role === "assistant" && (
                <span className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-cobalt-500 to-violet-500 text-white grid place-items-center">
                  <Bot className="h-4 w-4" />
                </span>
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                t.role === "user" ? "bg-cobalt-500 text-white rounded-br-sm" : "bg-muted rounded-bl-sm"
              }`}>
                <p className="whitespace-pre-wrap">{t.content}</p>
                {t.role === "assistant" && t.sources?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-black/10 dark:border-white/10 flex flex-wrap gap-1.5">
                    {t.sources.map((s, si) => (
                      <a key={si} href={s.uri} target="_blank" rel="noreferrer"
                        className="text-[11px] px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 hover:underline truncate max-w-[160px]"
                        title={s.title}>
                        {s.title || (() => { try { return new URL(s.uri).hostname; } catch { return s.uri; } })()}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              {t.role === "user" && (
                <span className="h-7 w-7 shrink-0 rounded-full bg-muted grid place-items-center">
                  <User className="h-4 w-4" />
                </span>
              )}
            </div>
          ))}
          {turns.map((t, i) => t.role === "assistant" && t.resourceMessage ? (
            <div key={`res-${i}`} className="ml-9 -mt-2 rounded-xl border-2 border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-950/30 p-3.5 max-w-[80%]">
              <div className="flex items-center gap-1.5 text-red-700 dark:text-red-400 text-xs font-bold mb-1.5">
                <ShieldAlert className="h-3.5 w-3.5" /> {t.crisis ? "Please read — support is available" : "This may need more than chat"}
              </div>
              <p className="text-sm leading-relaxed text-red-900 dark:text-red-200">{t.resourceMessage}</p>
              <Button
                type="button" size="sm" disabled={sendingReport}
                onClick={() => sendToAdmins(t)}
                className="mt-3 rounded-full h-8 bg-red-600 hover:bg-red-700 text-white"
              >
                {sendingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Send this to admins
              </Button>
            </div>
          ) : null)}
          {busy && (
            <div className="flex gap-2.5 justify-start">
              <span className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-cobalt-500 to-violet-500 text-white grid place-items-center">
                <Bot className="h-4 w-4" />
              </span>
              <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm text-muted-foreground inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="border-t p-3 flex items-end gap-2">
          <Textarea
            value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Type anything — a problem, a question, what's going on…"
            rows={1}
            className="rounded-xl resize-none min-h-[44px] max-h-32"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <Button type="button" onClick={send} disabled={busy || !input.trim()} className="rounded-full h-10 w-10 p-0 bg-cobalt-500 hover:bg-cobalt-600 text-white shrink-0">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3 max-w-2xl">
        This assistant can offer general guidance but isn't a substitute for a counselor, doctor, or campus authority.
        For anything urgent involving your safety, contact campus security or emergency services directly.
      </p>
    </Shell>
  );
}
